/**
 * InspectionContent - 检验报告渲染组件
 */

import React, { useState, lazy, Suspense } from 'react';
import { Paperclip, Download, PreviewOpen, FilePdf } from '@icon-park/react';
import { getProxyUrl } from '../../utils/urlProxy';

const PdfPreviewModal = lazy(() => import('../common/PdfPreviewModal'));
const PDFViewerMobile = lazy(() => import('../common/PDFViewerMobile'));

export default function InspectionContent({ data, mode = 'view', templateType }) {
  const [previewFile, setPreviewFile] = useState(null);
  const [showPcPreview, setShowPcPreview] = useState(false);
  const [showMobilePreview, setShowMobilePreview] = useState(false);

  console.log('[InspectionContent] 渲染检验报告组件, data:', data);

  if (!data || !data.attachments || data.attachments.length === 0) {
    console.log('[InspectionContent] 没有附件数据');
    return null;
  }

  const enabledAttachments = data.attachments.filter(att => att.enabled !== false);
  console.log('[InspectionContent] 启用的附件列表:', enabledAttachments);

  if (enabledAttachments.length === 0) {
    console.log('[InspectionContent] 所有附件都被禁用');
    return null;
  }

  const isStandardTemplate = templateType === 'standard-food-label';
  const isBlueWhiteTemplate = templateType === 'blue-white-template';
  // 尊重用户在编辑器中的选择：如果显式设置了 buttonStyle（对象/空），按此渲染；
  // 否则在未设置时才按模板默认（标准模板/蓝白 → 按钮，其它 → 列表）
  const hasExplicitChoice = data.hasOwnProperty('buttonStyle');
  const showButtons = hasExplicitChoice ? !!data.buttonStyle : (isStandardTemplate || isBlueWhiteTemplate);

  const isMobileDevice = () => typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // 打开预览
  const handlePreview = (attachment, e) => {
    console.log('[InspectionContent] 点击预览按钮, attachment:', attachment);
    if (attachment.file?.url) {
      e.preventDefault();
      console.log('[InspectionContent] 原始文件URL:', attachment.file.url);
      // 使用代理URL避免CORS问题
      const proxyUrl = getProxyUrl(attachment.file.url);
      console.log('[InspectionContent] 代理URL:', proxyUrl);
      const previewData = {
        fileUrl: proxyUrl,
        fileName: attachment.name || '检验报告'
      };
      console.log('[InspectionContent] 设置预览文件数据:', previewData);
      setPreviewFile(previewData);
      if (isMobileDevice()) {
        setShowMobilePreview(true);
      } else {
        setShowPcPreview(true);
      }
    } else {
      console.warn('[InspectionContent] ⚠️ 附件没有文件URL!', attachment);
    }
  };

  // 关闭预览
  const handleClosePreview = () => {
    setShowPcPreview(false);
    setShowMobilePreview(false);
    setPreviewFile(null);
  };

  // 下载文件
  const handleDownload = (attachment, e) => {
    console.log('[InspectionContent] 点击下载按钮, attachment:', attachment);
    if (attachment.file?.url) {
      e.preventDefault();
      e.stopPropagation();
      // 使用代理URL避免CORS问题
      const proxyUrl = getProxyUrl(attachment.file.url);
      console.log('[InspectionContent] 下载文件, 代理URL:', proxyUrl);
      // 创建下载链接
      const link = document.createElement('a');
      link.href = proxyUrl;
      link.download = attachment.name || '检验报告';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      console.log('[InspectionContent] 下载链接已触发');
    }
  };

  // 展示为按钮形式
  if (showButtons) {
    const style = data.buttonStyle || {};
    const variant = style.variant || 'gold';

    // 根据风格生成按钮样式
    let buttonStyle;
    if (variant === 'gold') {
      // 金色渐变
      const bgStart = style.bgStart || '#d79a3d';
      const bgEnd = style.bgEnd || '#b7791f';
      buttonStyle = {
        background: `linear-gradient(180deg, ${bgStart} 0%, ${bgEnd} 100%)`,
        color: style.textColor || '#fff',
        border: 'none',
        borderRadius: (style.radius || 10) + 'px',
        boxShadow: style.shadow === false ? 'none' : '0 6px 12px rgba(183,121,31,0.25)',
        padding: '12px 24px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center'
      };
    } else if (variant === 'blue') {
      // 蓝色渐变
      const bgStart = style.bgStart || '#2563EB';
      const bgEnd = style.bgEnd || '#1D4ED8';
      buttonStyle = {
        background: `linear-gradient(180deg, ${bgStart} 0%, ${bgEnd} 100%)`,
        color: style.textColor || '#fff',
        border: 'none',
        borderRadius: (style.radius || 10) + 'px',
        boxShadow: style.shadow === false ? 'none' : '0 6px 12px rgba(37,99,235,0.25)',
        padding: '12px 24px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center'
      };
    } else {
      // 自定义
      const bgStart = style.bgStart || '#d79a3d';
      const bgEnd = style.bgEnd || '#b7791f';
      buttonStyle = {
        background: `linear-gradient(180deg, ${bgStart} 0%, ${bgEnd} 100%)`,
        color: style.textColor || '#fff',
        border: 'none',
        borderRadius: (style.radius || 10) + 'px',
        boxShadow: style.shadow === false ? 'none' : '0 6px 12px rgba(0,0,0,0.15)',
        padding: '12px 24px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center'
      };
    }

    return (
      <>
        <div className="label-content-inspection">
          <div className="inspection-buttons">
            {enabledAttachments.map((att, idx) => {
              if (att.file?.url) {
                return (
                  <button
                    key={idx}
                    className="inspection-button"
                    style={buttonStyle}
                    onClick={(e) => handlePreview(att, e)}
                  >
                    <FilePdf theme="outline" size={18} fill="currentColor" strokeWidth={3} style={{ marginRight: '8px' }} />
                    {att.name}
                  </button>
                );
              } else {
                return (
                  <button key={idx} className="inspection-button" style={{...buttonStyle, opacity: 0.6, cursor: 'not-allowed'}} disabled>
                    <FilePdf theme="outline" size={18} fill="currentColor" strokeWidth={3} style={{ marginRight: '8px', opacity: 0.6 }} />
                    {att.name}
                  </button>
                );
              }
            })}
          </div>
        </div>
        {/* 预览模态框 */}
        {showPcPreview && previewFile && (
          <Suspense fallback={null}>
            <PdfPreviewModal
              fileName={previewFile.fileName}
              fileUrl={previewFile.fileUrl}
              onCancel={handleClosePreview}
              open={showPcPreview}
            />
          </Suspense>
        )}
        {showMobilePreview && previewFile && (
          <Suspense fallback={null}>
            <PDFViewerMobile
              visible={showMobilePreview}
              onClose={handleClosePreview}
              fileUrl={previewFile.fileUrl}
              fileName={previewFile.fileName}
            />
          </Suspense>
        )}
      </>
    );
  }

  // 默认列表形式
  const listBorderColor = data.borderColor || '#e8eaed';

  return (
    <>
      <div
        className="label-content-inspection"
        style={{
          borderColor: listBorderColor,
          borderWidth: '1px',
          borderStyle: 'solid',
          borderRadius: '8px',
          padding: '12px'
        }}
      >
        <h3 className="content-section-heading">检验报告</h3>
        <div className="inspection-list">
          {enabledAttachments.map((att, idx) => (
            <div key={idx} className="inspection-item">
              <Paperclip theme="outline" size={16} fill="#5f6368" strokeWidth={3} />
              {att.file?.url ? (
                <div className="inspection-item-content">
                  <button
                    className="inspection-name inspection-link"
                    onClick={(e) => handlePreview(att, e)}
                    title="点击预览"
                  >
                    {att.name}
                    <PreviewOpen theme="outline" size={14} fill="#1890ff" strokeWidth={3} style={{ marginLeft: '4px' }} />
                  </button>
                  <button
                    className="inspection-download-icon"
                    onClick={(e) => handleDownload(att, e)}
                    title="下载"
                  >
                    <Download theme="outline" size={14} fill="#666" strokeWidth={3} />
                  </button>
                </div>
              ) : (
                <span className="inspection-name">{att.name}</span>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* 预览模态框 */}
      {showPcPreview && previewFile && (
        <Suspense fallback={null}>
          <PdfPreviewModal
            fileName={previewFile.fileName}
            fileUrl={previewFile.fileUrl}
            onCancel={handleClosePreview}
            open={showPcPreview}
          />
        </Suspense>
      )}
      {showMobilePreview && previewFile && (
        <Suspense fallback={null}>
          <PDFViewerMobile
            visible={showMobilePreview}
            onClose={handleClosePreview}
            fileUrl={previewFile.fileUrl}
            fileName={previewFile.fileName}
          />
        </Suspense>
      )}
    </>
  );
}
